import { ElementRef, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UserDataResult } from 'angular-auth-oidc-client';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { IBuilding, MOCK_BUILDINGS } from '../core/mockData';
import { Command, MessageType, ToolId, ViewContext } from '../enums';
import {
	AssetListState,
	CompassHeadingState,
	ConnectState,
	FilterForm,
	HoverChartState,
	MessageData,
	OnHoverEvent,
	REIInsightsData,
	SetViewContextEvent,
	ToolSelectState,
	UserHistory,
	UserPreference,
	ViewContextState,
} from '../interfaces';
import { CameraRangeAlert } from '../interfaces/camera-range-alert';
import { loadHoverData, resetHoverData } from '../state/hover-chart.actions';
import { HttpService } from './http/http.service';
import { UserInput } from '../interfaces/user-input';
import { HeatMapEvent } from '../interfaces/heat-map-event';
import { ToastrTypes } from '../interfaces/toastr-types';
import { ToastrMessages } from '../enums/toastr-messages';
import { BoundaryService } from './boundary.service';
import { AppType } from '../enums/app-type';
import { CenterWrapperComponents } from '../interfaces/center-wrapper-components';
import { DeviceType } from '../interfaces/device-type';
import { FilterFormREI } from '../interfaces/filter-form-rei';
import { reiFilterForm } from '../constants/reiFilterForm';
import { APP_TYPE } from '../constants/appType';
import { AssetSelected } from '../interfaces/asset-selected';
import { REIHeatmapFilters } from '../interfaces/reiHeatmapFilters';
import { environment } from 'src/environments/environment';
import { SelectedREIFilterDropdown } from '../interfaces/selected-reifilter-dropdown';
import { lbmFilterForm } from '../constants/lbmFilterForm';
import {
	HeatmapFormREI,
	HeatmapValuesREI,
	PostRentandSales,
	REIHeatmapInsights,
} from '../interfaces/heatmapFormREI';
import { DefaultReiHeatmapSchema } from '../constants/reiHeatmapForm';
import { addHistory } from '../state/history.actions';
import { CREATOR } from '../constants/creator';
import { allowedREIPlots } from '../constants/allowedREIPlots';
import { C4Context } from '../constants/c4Context';
import { KpisCategories } from '../types/kpisData';

declare const xrsendBitrateRequest: any;
declare const sendToStreamer: any;

@Injectable({
	providedIn: 'root',
})
export class MessageHandlerService {
	public defaultViewContext = {
		CurrentFocus: ViewContext.City,
		Country: 'UAE',
		City: '',
		Community: '',
		Sector: '',
		Plot: '',
		Building: '',
		Floor: '',
		Unit: '',
		Asset: '',
	};
	levels = [
		ViewContext.City,
		ViewContext.Sector,
		ViewContext.Community,
		ViewContext.Plot,
		ViewContext.Building,
		ViewContext.Asset,
	];
	public playButton!: ElementRef<HTMLButtonElement>;
	public overlayWrapper!: ElementRef<HTMLDivElement>;
	public isDashboard: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isLoadingComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showReportPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public toggleReportPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public toggleHoverPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleInsightsPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public hideInsightsPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleFeasibilityPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleFeasibility: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public gatewayDetailsPanel: Subject<boolean> = new Subject<boolean>();
	public embedGatewayPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public embedContilioPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public embedContilioUrl: BehaviorSubject<string> = new BehaviorSubject<string>('https://app.contilio.com/');
	public gatewayDetailsTwoUrl: BehaviorSubject<string> = new BehaviorSubject<string>(environment.embedGateWayUrl);
	public gatewayListView: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public gatewaySource: BehaviorSubject<string> = new BehaviorSubject<string>('default');
	public selectLocationMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public collapseInsightsPanel: Subject<boolean> = new Subject<boolean>();
	public filteredCountTitle: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public filteredAreas: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	public compassAngle: BehaviorSubject<number> = new BehaviorSubject<number>(0.0);
	public toggleZoomSliderPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleSpeedLevelScalePanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		false,
	);
	public fetchHistory: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleScaleMapPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleBreadCrumbPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public gatewayLocationSelected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleFullScreenView: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public toggleCompass: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public toggleFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleHeatMap: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public filtersAvailable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public heatMapAvailable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showInsightsHubMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public filteredData: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public freezeTooltip: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public currentUser: BehaviorSubject<UserDataResult> = new BehaviorSubject<any>({
		userData: {},
	});
	public heatMapInsightsActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public currentFilterAreas: BehaviorSubject<any> = new BehaviorSubject<any>([]);
	public isRedirectedFromNoStreamer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		false,
	);
	public gatewayAreaDetails: Subject<number> = new Subject<number>();

	public zoomLevel: BehaviorSubject<CameraRangeAlert> = new BehaviorSubject<CameraRangeAlert>({
		CameraDistanceMin: 50000,
		CameraDistanceMax: 30222332,
		CameraDistance: 30222332,
	});
	public triggerSetBreadcrumbs: Subject<ViewContext> = new Subject<ViewContext>();
	public showGameSpinner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public toastrStaticMessage: any = ToastrMessages;
	public toasterMessage: BehaviorSubject<ToastrTypes> = new BehaviorSubject<ToastrTypes>({
		type: 'info',
		message: this.toastrStaticMessage.empty,
	});
	public selectedHeatMap: BehaviorSubject<[string, string]> = new BehaviorSubject<
		[string, string]
	>(['', '']);
	public selectedHeatMapREI: BehaviorSubject<[string, string, string]> = new BehaviorSubject<
		[string, string, string]
	>(['', '', '']);
	public selectedFilters: BehaviorSubject<FilterForm> = new BehaviorSubject<FilterForm>({
		...lbmFilterForm,
	});
	public selectedREIFilters: BehaviorSubject<FilterFormREI> = new BehaviorSubject<FilterFormREI>({
		...reiFilterForm,
	});
	public viewContextState: BehaviorSubject<ViewContextState> =
		new BehaviorSubject<ViewContextState>({
			...this.defaultViewContext,
		});
	public currentViewContextState: string = ViewContext.City;
	public hoverChartState: BehaviorSubject<HoverChartState> = new BehaviorSubject<HoverChartState>(
		{
			AssetID: '',
			ObjectType: ViewContext.City,
			CameraDistance: '',
			CameraDistanceMax: '',
			CameraDistanceMin: '',
		},
	);
	public selectedBuildings: BehaviorSubject<IBuilding[]> = new BehaviorSubject<IBuilding[]>([]);
	public toolSelectState: BehaviorSubject<ToolSelectState> = new BehaviorSubject<ToolSelectState>(
		{
			ToolId: ToolId.Leasing_and_Transactions,
			Selected: true,
		},
	);
	public compassVectors: BehaviorSubject<CompassHeadingState> =
		new BehaviorSubject<CompassHeadingState>({
			NorthAngle: 0.0,
		});
	public bitrateRequestFlag: boolean = false;
	public startApp: Subject<boolean> = new Subject<boolean>();
	public isFilterApplied: boolean = false;
	public isHeatMap: boolean = false;
	lastLevel: ViewContext = ViewContext.City;
	public filterMessage: { cmd: string; type: string; msg: any } | null | undefined;
	isOulineMapImplemented: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	isHeatMapImplemented: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	heatMapData: BehaviorSubject<any> = new BehaviorSubject<any>({});
	filtersLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	heatmapLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	openInsightHub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	showHeatMapCardandFilterCard: Subject<boolean> = new Subject<boolean>();
	filtersViewContext: BehaviorSubject<ViewContextState> = new BehaviorSubject<ViewContextState>({
		...this.defaultViewContext,
	});
	heatMapViewContext: BehaviorSubject<ViewContextState> = new BehaviorSubject<ViewContextState>({
		...this.defaultViewContext,
	});
	applicationType: BehaviorSubject<string> = new BehaviorSubject<string>(APP_TYPE.LBM);
	loadApplicationTypeSummary: Subject<boolean> = new Subject<boolean>();
	filtersApi: BehaviorSubject<FilterForm | null> = new BehaviorSubject<FilterForm | null>(null);
	filtersApiREI: BehaviorSubject<FilterFormREI | null> =
		new BehaviorSubject<FilterFormREI | null>(null);
	heatMapApi: BehaviorSubject<[string, string] | null> = new BehaviorSubject<
		[string, string] | null
	>(null);
	heatMapApiREI: BehaviorSubject<[string, string, string] | null> = new BehaviorSubject<
		[string, string, string] | null
	>(null);
	heatLvls: BehaviorSubject<[number, number]> = new BehaviorSubject<[number, number]>([0, 0]);
	heatmapMsg: any;
	historyDetails: BehaviorSubject<ViewContextState | null> =
		new BehaviorSubject<ViewContextState | null>(null);
	requestHistoryUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	userInputMessage: BehaviorSubject<UserInput> = new BehaviorSubject<UserInput>({
		panning: false,
		orbiting: false,
		zooming: false,
		panspeed: 0,
		zoomspeed: 0,
	});
	public isUpdatingHIstory: boolean = false;
	public startTour = false;
	public closeSearch: Subject<boolean> = new Subject<boolean>();
	public gatewaySelectedLocation: Subject<any> = new Subject<any>();
	public GatewaySubmitData: Subject<any> = new Subject<any>();
	public GatewayReviewSubmit: Subject<any> = new Subject<any>();
	public GatewayReviewComment: Subject<any> = new Subject<any>();
	public searchLoading: Subject<boolean> = new Subject<boolean>();
	public appMode: BehaviorSubject<string> = new BehaviorSubject<string>(AppType.LBM);

	public userPreference: BehaviorSubject<UserPreference> = new BehaviorSubject<UserPreference>(
		{},
	);
	public overlayRemoved: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showHeatMapSummary: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public disableFilters: Subject<boolean> = new Subject<boolean>();
	public reloadFilters: Subject<boolean> = new Subject<boolean>();
	public editFilters: Subject<boolean> = new Subject<boolean>();
	public editHeatmap: Subject<boolean> = new Subject<boolean>();
	public disableHeatmap: Subject<boolean> = new Subject<boolean>();
	public heatmapError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public navigateToPreviousContext: Subject<boolean> = new Subject<boolean>();
	public microStrategyLoginStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public landingPageStaticContent: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public enableSidebar: Subject<boolean> = new Subject<boolean>();
	public overlayWrapperState: Subject<boolean> = new Subject<boolean>();
	public faqStaticContent: any;
	public postLoginContent: any;
	public dashboardStaticContent: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public insightsStaticContent: any;
	public toastrMessages: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public tooltips: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public glossaries: BehaviorSubject<any> = new BehaviorSubject<any>({});
	public renderCenterWrapper: BehaviorSubject<CenterWrapperComponents> =
		new BehaviorSubject<CenterWrapperComponents>({
			isCenterWrapperActive: false,
			isFaqActive: false,
			isGlossaryActive: false,
			isReportProblemActive: false,
			isBreadcrumbActive: false,
		});
	public deviceType: BehaviorSubject<DeviceType> = new BehaviorSubject<DeviceType>({
		isMobile: false,
		isTablet: false,
		isDesktop: false,
		isPortrait: false,
	});
	public logout: Subject<boolean> = new Subject<boolean>();
	public isInAssetSelectedMode: boolean = false;
	public isHeatmapModified: boolean = false;
	public reiHeatmapFilters: REIHeatmapFilters = {
		Buildings: [],
		HospitalitySubType: [],
		SubCategories: [],
		SupplyAndDemand: [],
		Rent: [],
		Sales: [],
		RentAndSalesSubCategories: [],
	};
	public selectedHeatmapType = '';
	public isBoundaryDisableForCommunity: boolean = false;
	public isBoundaryDisableForPlot: boolean = false;
	public switchToBuildingFromCommunity: boolean = false;
	public assetToHighlight: string | ViewContextState = '';
	public isREIPlotLevel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	// public switchToBuildingFromCommunityBySearch: boolean = false;
	// public contextToSwitchToBuildingFromCommunityBySearch: any = {};
	public selectedREIFilterDropdown: BehaviorSubject<SelectedREIFilterDropdown> =
		new BehaviorSubject<SelectedREIFilterDropdown>({
			currentFilterCategoryIndex: 0,
			currentSupplyAndDemandSubCategoryIndex: 0,
			currentResidentialSubCategoryIndex: 0,
			currentHospitalitySubCategoryIndex: 0,
		});

	public isNormalLogout: boolean = false;
	public isNavigationBySearch: boolean = false;
	public isNavigationByBreadcrumb: boolean = false;
	public isNavigationByHistory: boolean = false;

	public heatmapSummaryClosed: Subject<boolean> = new Subject<boolean>();
	public hideHoverPopup: Subject<boolean> = new Subject<boolean>();

	heatmapScehema: HeatmapValuesREI = {
		...DefaultReiHeatmapSchema,
	};
	public insightsDataForHeatmap: BehaviorSubject<REIHeatmapInsights> =
		new BehaviorSubject<REIHeatmapInsights>({
			supplyAndDemand: { name: '', values: [''] },
			rentAndSales: {},
		});
	public MSTRSpinnerOverlayState: Subject<boolean> = new Subject<boolean>();
	public tempViewContextBSK: ViewContextState | null = null;
	public disableAnalytics: Subject<boolean> = new Subject<boolean>();
	public disableFilterAreasPopup: Subject<boolean> = new Subject<boolean>();
	public sideBarMenuCollapse: Subject<boolean> = new Subject<boolean>();
	summarySubscription: Subscription | null = null;
	messageToIframeToDisableCursor={"message": {"type": "togglehoveringmouse", "value": false}};
	messageToIframeToEnableCursor={"message": {"type": "togglehoveringmouse", "value": true}};
	public flyMode: Subject<boolean> = new Subject<boolean>();
	flyModeMouseSpeed: number = 1;
	public toggleKpiViewType: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleKpiMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleKpiConsole: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleKpiSummaryExtended: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public toggleKpiAlerts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public kpiAlertsOrigin: BehaviorSubject<'summary' | 'extended'> = new BehaviorSubject<'summary' | 'extended'>('extended');
	public kpiAlertsSeverityFilter: BehaviorSubject<string> = new BehaviorSubject<string>('All severities');
	public toggleKpiSelectedView: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public kpiSelectedView: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public kpiSelectedCategory: BehaviorSubject<KpisCategories> = 
		new BehaviorSubject<KpisCategories>({
			name: '',
			id: 0,
		});
	public kpiSelectedLocation: BehaviorSubject<string> = new BehaviorSubject<string>('');
	public kpiSelectedLocationName: BehaviorSubject<string> = new BehaviorSubject<string>('');

	constructor(
		private _http: HttpService,
		private store: Store<HoverChartState>,
		private historyStore: Store<UserHistory>,
		private _boundaryService: BoundaryService,
	) { }

	public setRefs(
		playButton: ElementRef<HTMLButtonElement>,
		overlayWrapper: ElementRef<HTMLDivElement>,
	): void {
		this.playButton = playButton;
		this.overlayWrapper = overlayWrapper;
	}

public  DEFAULT_PAN_SPEED =30;
public  contillioPopup:Subject<boolean> = new Subject<boolean>();
// public  contillioProjectIsland:BehaviorSubject<string> = new BehaviorSubject<string>("");
public  contillioProjectIsland:Subject<string> = new Subject<string>();
	sendThisMessageToStreamer(message: any) {

console.log("Sending message to streamer:", message);
		sendToStreamer(message);
	}

	SetApplicationType(name: string) {
		const message = {
			cmd: 'AppSelect',
			type: 'notif',
			msg: { name },
		};


		this.applicationType.next(name);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
		this.requestHistoryUpdate.next(true);
	}
		streamSetViewContext(
		msg: ViewContextState,
		showSpinner: boolean = true,
		creator: string = CREATOR.Default,
	): void {
		if (showSpinner) {
			this.showGameSpinner.next(true);
		}
		const context = structuredClone(msg);
		const message: SetViewContextEvent = {
			type: MessageType.notif,
			cmd: Command.SetViewContext,
			msg: context,
		};
		message.msg.Creator = creator;
		try {
			//sendToStreamer(message);
			this.sendThisMessageToStreamer(message);
			console.log('Set ViewContext sent', message);
		} catch (error) {
			console.log('ViewContext not sent', message);
		}
	}

	public handleEvent(event: MessageEvent<MessageData>, eventDataIs: any): void {
		try {
			eventDataIs = JSON.parse(eventDataIs);
		}
		catch(ex) {
		}
		
		
		if (!eventDataIs.type) return;
		switch(eventDataIs.type) {
			case MessageType.response:
				this.handleMessageTypeResponse(eventDataIs);
				break;
			case MessageType.notif:
				this.handleMessageTypeNotif(event, eventDataIs);
				break;
			case MessageType.streamState:
				this.handleMessageTypeStreamState(eventDataIs);
				break;
			default:
				console.debug(`received event ${eventDataIs}`);
				break;
		}
	}
	handleMessageTypeStreamState(eventDataIs: any): void {
		if (eventDataIs.value === "connecting") {
			this.isConnected.next(false);
			this.isLoadingComplete.next(false);
		}
		if (eventDataIs.value === "loadingComplete") {
			this.isLoadingComplete.next(true);
			// Hide loading video after a delay to allow progress bar to complete
			setTimeout(() => {
				this.isConnected.next(true);
			}, 1000); // Slightly longer than the progress completion animation
		}
	}
	handleMessageTypeResponse(eventDataIs: any): void {

		switch (eventDataIs.cmd) {
			case 'connect':
				setTimeout(() => {
					this.isConnected.next(
						(eventDataIs.msg as ConnectState).status === 'connected' ||
						(eventDataIs.msg as ConnectState).status === 'AlreadyConnected',
					);
				}, 2000);
				this.clickPlay(0);
				this.getViewContext();
				this.startApp.next(true);
				setTimeout(() => {
					if (!this.bitrateRequestFlag) {
						xrsendBitrateRequest(20_000_000);
						this.bitrateRequestFlag = true;
					}
				}, 5000);
				break;
			case 'SetInteractionMode':
				if (this.heatMapAvailable.value && this.isHeatMap && this.heatmapMsg) {
					this.lastLevel = this.heatmapMsg.msg.ViewContext;
					//sendToStreamer(this.heatmapMsg);
					this.sendThisMessageToStreamer(this.heatmapMsg);
				}
				if (this.filtersAvailable.value && this.isFilterApplied && this.filterMessage) {
					this.lastLevel = this.filterMessage.msg.ViewContext;
					console.log('this.filterMessage:', this.filterMessage);
					//sendToStreamer(this.filterMessage);
					this.sendThisMessageToStreamer(this.filterMessage);
				}
				if (
					!(this.heatMapAvailable.value && this.isHeatMap && this.heatmapMsg) &&
					!(this.filtersAvailable.value && this.isFilterApplied && this.filterMessage)
				) {
					if (this.tempViewContextBSK) {
						this.streamSetViewContext(
							this.tempViewContextBSK,
							true,
							CREATOR.Blackshark,
						);
						this.tempViewContextBSK = null;
					} else {
						this.tempViewContextBSK = null;
					}
				}

				break;
			case 'Outlinemap':
				this.showGameSpinner.next(false);
				this.isOulineMapImplemented.next(true);
				break;
			case 'Heatmap':
				const msg = eventDataIs.msg as HeatMapEvent;
				if (
					(this.applicationType.value === APP_TYPE.LBM ||
						this.applicationType.value === APP_TYPE.REI) &&
					msg.status === false &&
					this.currentViewContextState != ViewContext.Plot &&
					this.currentViewContextState != ViewContext.Building &&
					this.currentViewContextState != ViewContext.Unit &&
					this.currentViewContextState != ViewContext.Floor
				) {
					//?:if heat map cannot apply on community disable heatmap | not needed for now
					//?:if heatmap applied and we go to plot level then dont disable heat map of previos level
					// this.heatMapAvailable.next(false);
					// this.resetInteractions(true, false);
					this.toasterMessage.next({
						type: 'info',
						message: this.toastrStaticMessage.cannotApplyHeatMap,
					});
					this.heatmapError.next(true); // hide heatmap summary if the heatmap can't be applied
					this.showGameSpinner.next(false);
					// this.switchtoPreviousViewContextHeatMap();
					// this.navigateToPreviousContext.next(true);
					//?above line commented becuuase if we just apply land ares(sqm) to SEIH AL SEDEIRAH 1. it goes to previous history like yas island.
				} else {
					this.isHeatMapImplemented.next(true);
					setTimeout(() => {
						this.showGameSpinner.next(false);
					}, 1000);
				}
				if (this.tempViewContextBSK) {
					this.streamSetViewContext(this.tempViewContextBSK, true, CREATOR.Blackshark);
					this.tempViewContextBSK = null;
				} else {
					this.tempViewContextBSK = null;
				}

				break;
		}
	}
	//sending message to Iframe 
	sendMessageToIframe = (message: any) => {		
		const player = document.querySelector('#player') as HTMLIFrameElement;
		if (player) {
			player!.contentWindow!.postMessage(message, 'https://stream.streampixel.io');
		}
	}
	handleMessageTypeNotif(event: MessageEvent<MessageData>, eventDataIs: any): void {


		switch (eventDataIs.cmd) {
			case 'AssetList':
				const selectedBuildings = MOCK_BUILDINGS.filter((building) =>
					(eventDataIs.msg as AssetListState).Building?.includes(building.id),
				);
				console.log('Selected buildings: ', selectedBuildings);

				if (selectedBuildings.length > 0) {
					this.selectedBuildings.next(selectedBuildings);
				}
				this.freezeTooltip.next(selectedBuildings.length > 0);
				break;
			case 'CameraRangeAlert':
				const cameraAlert = eventDataIs.msg as CameraRangeAlert;

				const cameraDistance = this.cameraDistanceStringToNumber(
					cameraAlert.CameraDistance,
				);
				const cameraDistanceMax = this.cameraDistanceStringToNumber(
					cameraAlert.CameraDistanceMax,
				);
				const cameraDistanceMin = this.cameraDistanceStringToNumber(
					cameraAlert.CameraDistanceMin,
				);
				this.zoomLevel.next({
					CameraDistanceMin: cameraDistanceMin,
					CameraDistanceMax: cameraDistanceMax,
					CameraDistance: cameraDistance,
				});
				if (cameraDistanceMax === cameraDistance) {
					const viewContext = this.viewContextState.value;
					if (this.checkIfSwitchableToPreviousContextByCameraDistance(viewContext)) {
						this.switchtoPreviousViewContext();
					}
				}
				break;
			case 'ViewContext':
				this.handleViewContext(event, eventDataIs);
				break;
			case 'OnHover':
			
				const onHover = eventDataIs as OnHoverEvent;
				if (onHover.msg.CameraDistance) {
					let currentCamDistance = +onHover.msg.CameraDistance.replace(/,/g, '');
					const CameraDistanceMax = +onHover.msg.CameraDistanceMax.replace(/,/g, '');
					const CameraDistanceMin = +onHover.msg.CameraDistanceMin.replace(/,/g, '');
					if (currentCamDistance < 0) {
						console.log('Got Minus value CameraDistance', currentCamDistance);
						currentCamDistance = currentCamDistance * -1;
						this.setZoomLevel(currentCamDistance);
					}
					this.zoomLevel.next({
						CameraDistanceMin: CameraDistanceMin,
						CameraDistanceMax: CameraDistanceMax,
						CameraDistance: currentCamDistance,
					});
				}
				if (
					this.applicationType.value === APP_TYPE.LBM &&
					onHover.msg.ObjectType === ViewContext.Building
				) {
					//?if we are in lbm and we receive object type as building then return
					return;
				}
				if (
					this.applicationType.value === APP_TYPE.REI &&
					onHover.msg.ObjectType === ViewContext.Plot
				) {
					//?return if received plot hover message if in rei mode
					return;
				}
				// In Operations Management, hovering AldarSquare floors should set a Floor view context
				if (
					this.applicationType.value === APP_TYPE.OM &&
					onHover.msg.ObjectType === ViewContext.Floor &&
					onHover.msg.AssetID &&
					onHover.msg.AssetID.startsWith('0213304572_E501-O1_')
				) {
					// Base FloorData for AldarSquare; only Floor changes per hovered AssetID
					const aldarSquareFloorBase: ViewContextState = {
						CurrentFocus: ViewContext.Floor,
						Country: 'UAE',
						City: '02',
						Sector: '021330',
						Community: 'C_0213304572',
						Plot: '0213304572_E501',
						Building: '0213304572_E501-O1',
						Floor: '',
						Unit: '',
						Asset: '',
					};
					const newContext: ViewContextState = {
						...aldarSquareFloorBase,
						Floor: onHover.msg.AssetID,
					};
					if (this.viewContextState.value.Floor !== newContext.Floor) {
						this.streamSetViewContext(newContext, true, CREATOR.Default);
					}
				}
				this.hideHoverPopup.next(false);
				this.hoverChartState.next(onHover.msg);
				this.store.dispatch(
					loadHoverData({
						layerType: onHover.msg.ObjectType.toLowerCase() as Lowercase<ViewContext>,
						id: onHover.msg.AssetID,
						cityId: this.viewContextState.value.City,
					}),
				);
				break;
			case 'ToolSelect':
				const toolSelect = eventDataIs.msg as ToolSelectState;
				this.toolSelectState.next(toolSelect);
				console.log('ToolSelect: ', toolSelect);
				break;
			case 'CompassHeading':
				const compassVectors = eventDataIs.msg as CompassHeadingState;
				this.compassAngle.next(compassVectors.NorthAngle);
				break;
			case 'GWSavedData':
				console.log('GWSavedData',eventDataIs.msg);
				this.gatewaySelectedLocation.next(eventDataIs.msg)
				break;
			case 'UtilsScreenShot':
				console.log('UtilsScreenShot',eventDataIs.msg);
				break;
			case 'UserInput':
				const userInputMessage = eventDataIs.msg as UserInput;
				this.userInputMessage.next(userInputMessage);
				let elem: HTMLElement | null = document.getElementById('playerUI');
				if (userInputMessage.panning === true) {
					elem?.setAttribute(
						'style',
						'cursor: grab; cursor: -moz-grab; cursor: -webkit-grab;',
					);
				} else if (userInputMessage.orbiting === true) {
					elem?.setAttribute(
						'style',
						'cursor: url("../../assets/images/orbit.svg"), auto',
					);
				} else if (!userInputMessage.panning || !userInputMessage.orbiting) {
					elem?.setAttribute('style', 'cursor: auto');
				}

				if (userInputMessage.zooming === true) {
					//?if we are in c4 builing and current camera distance and max camera distance is same then  switch to previous context
					const viewContext = this.viewContextState.value;
					this.checkToSwitchBackToPreviousContextInC4(viewContext);
				}
				break;
			case 'SetViewContextError':
				this.showGameSpinner.next(false);
				this.toasterMessage.next({
					type: 'error',
					message: this.toastrStaticMessage.cannotSetViewContext,
				});
				break;
			case 'AssetSelected':
				this.handleAssetSelected(event);
				break;

			case 'ConstructionManageSelect':
				console.log("ConstructionManageSelect notification received:", eventDataIs.msg);
				
					this.contillioProjectIsland.next(eventDataIs.msg.id);
					this.contillioPopup.next(true);
					break;
			case 'ConsumeCursor':
				console.log("ConsumeCursor notification received:", eventDataIs.msg);
				
					this.sendMessageToIframe(eventDataIs.msg.enable?this.messageToIframeToDisableCursor:this.messageToIframeToEnableCursor)
					break;
					
		}
	}

	checkIfSwitchableToPreviousContextByCameraDistance(viewContext: ViewContextState): boolean {
		//?ISSUE:on clicking floor of c4 we gets same camera distance for max and current, which inturn switches to previous context
		//? ISSUE : on set view context to floor or unit of c4 we get same camera distance for max and current, which inturn switches to previous context
		//?commenting below line . if we sent a set context to building from search if click a building this conflicts
		const isViewContextAfterBuilding =
			viewContext.context === ViewContext.Building ||
			viewContext.context === ViewContext.Floor ||
			viewContext.context === ViewContext.Unit;

		if (isViewContextAfterBuilding && viewContext.Building === C4Context.Building) {
			return false;
		}
		return true;
	}

	checkToSwitchBackToPreviousContextInC4(viewContext: ViewContextState): void {
		const isViewContextPlot = viewContext.context === ViewContext.Plot;
		const isViewContextBuilding = viewContext.context === ViewContext.Building;
		const isViewContextFloor = viewContext.context === ViewContext.Floor;
		const isViewContextUnit = viewContext.context === ViewContext.Unit;
		const isZoomMax =
			this.zoomLevel.value.CameraDistance === this.zoomLevel.value.CameraDistanceMax;
		if (isViewContextBuilding && viewContext.Building === C4Context.Building && isZoomMax) {
			const c4PlotContext: ViewContextState = {
				...C4Context,
				CurrentFocus: ViewContext.Plot,
			};
			this.streamSetViewContext(c4PlotContext as ViewContextState);
		}

		if (isViewContextFloor && viewContext.Building === C4Context.Building && isZoomMax) {
			const c4BuildingContext: ViewContextState = {
				...C4Context,
				CurrentFocus: ViewContext.Building,
			};
			this.streamSetViewContext(c4BuildingContext as ViewContextState);
		}

		if (isViewContextUnit && viewContext.Building === C4Context.Building && isZoomMax) {
			const c4FloorContext: ViewContextState = {
				...C4Context,
				Floor: viewContext.Floor,
				CurrentFocus: ViewContext.Floor,
			};
			this.streamSetViewContext(c4FloorContext as ViewContextState);
		}
		// if (isViewContextPlot && viewContext.Building === C4Context.Building && isZoomMax) {
		// 	const c4CommunityContext: ViewContextState = {
		// 		...C4Context,
		// 		CurrentFocus: ViewContext.Community,
		// 	};
		// 	this.streamSetViewContext(c4CommunityContext as ViewContextState);
		// }
	}

	handleAssetSelected(event: MessageEvent<MessageData>): void {
		const buildingIds = (event.data.msg as AssetSelected).Attributes.jll_building_id.split(',');

		// Only execute REI-specific logic when in REI mode, not in GIS or other modes
		if (this.isInREI() && buildingIds.length === 1) {
			this.showGameSpinner.next(false);
			const msg = event.data.msg as AssetSelected;
			if (
				(msg.Selected === 'true' &&
					(this.viewContextState.value.context === ViewContext.Community ||
						this.viewContextState.value.context === ViewContext.Building ||
						this.viewContextState.value.context === ViewContext.Plot)) ||
				this.isNavigationBySearch ||
				this.isNavigationByHistory
			) {
				this.isInAssetSelectedMode = true;
				const buildingId = msg.Attributes.jll_building_id;
				// if (!this.isNavigationBySearch || this.isNavigationByHistory) {
				// 	this.switchViewContextToBSKBuilding(buildingId);
				// } else {
				// 	if (this.filtersAvailable.value || this.heatMapAvailable.value) {
				// 		this.switchViewContextToBSKBuilding(buildingId);
				// 	} else {
				// 		this.setCustomViewContextForBuilding(buildingId);
				// 	}
				// }

				if (this.isNavigationBySearch) {
					if (this.filtersAvailable.value || this.heatMapAvailable.value) {
						this.switchViewContextToBSKBuilding(buildingId);
					} else {
						this.setCustomViewContextForBuilding(buildingId);
					}
				} else if (this.isNavigationByHistory) {
					if (this.filtersAvailable.value || this.heatMapAvailable.value) {
						this.switchViewContextToBSKBuilding(buildingId);
					} else {
						this.setCustomViewContextForBuilding(buildingId);
					}
				} else {
					this.switchViewContextToBSKBuilding(buildingId);
				}
				this.isNavigationBySearch = false;
				this.isNavigationByHistory = false;
			} else if (msg.Selected === 'false') {
				this.isInAssetSelectedMode = false;
				if (
					this.viewContextState.value.CurrentFocus === ViewContext.Building &&
					!this.isNavigationByBreadcrumb &&
					!this.isNavigationByHistory
				) {
					this.switchtoCommunityContext();
				}
			}
		} else {
			this.isInAssetSelectedMode = false;
		}
	}

	handleViewContext(event: MessageEvent<MessageData>, eventDataIs: any): void {
		this.isInAssetSelectedMode = false;
		this.isNavigationBySearch = false;
		this.isNavigationByBreadcrumb = false;
		this.isNavigationByHistory = false;

		const viewContext = eventDataIs as SetViewContextEvent;
		if (viewContext.msg.context) {
			viewContext.msg.CurrentFocus = viewContext.msg.context;
			viewContext.msg.is_bsk = false;
		}

		if (this.checkIfInREIPlot(viewContext) && !allowedREIPlots.includes(viewContext.msg.Plot)) {
			this.isREIPlotLevel.next(true);
			if (this.showGameSpinner.value) {
				this.showGameSpinner.next(false);
			}
			this.isBoundaryDisableForPlot = true;
			this.disableBoundaries();
			return;
		} else {
			if (this.isBoundaryDisableForPlot) this.enableBoundaryIfDisabled();
			this.isBoundaryDisableForPlot = false;
			this.isREIPlotLevel.next(false);
		}

		this.viewContextState.next(structuredClone(viewContext.msg));
		this.currentViewContextState = viewContext.msg.CurrentFocus;
		this.freezeTooltip.next(false);
		this.hoverChartState.next({
			AssetID: '',
			ObjectType: ViewContext.City,
			CameraDistance: this.hoverChartState.value.CameraDistance,
			CameraDistanceMax: this.hoverChartState.value.CameraDistanceMax,
			CameraDistanceMin: this.hoverChartState.value.CameraDistanceMin,
		});
		if (viewContext.msg.CurrentFocus !== ViewContext.Building || viewContext.msg.Plot !== '') {
			//? getting two viewcontext for building. one with plot and one without plot
			this.requestHistoryUpdate.next(true);
		}
		this.checkToEnableBoundary(viewContext);
		this.store.dispatch(resetHoverData());
		if (!this.filtersAvailable.value && !this.isHeatMap) {
			this.showGameSpinner.next(false);
		}
	}

	getLevelIndexDiffrenceHeatMap(viewContext: SetViewContextEvent['msg']): boolean {
		const heatMapLvlIndex = this.levels.indexOf(this.heatMapViewContext.value.CurrentFocus);
		const newLevelIndex = this.levels.indexOf(viewContext.CurrentFocus);
		if (heatMapLvlIndex < newLevelIndex) {
			if (
				viewContext.CurrentFocus === ViewContext.Community &&
				this.heatMapViewContext.value.CurrentFocus === ViewContext.Sector &&
				viewContext.Sector !== this.heatMapViewContext.value.Sector
			) {
				//? Yas island to rs3. They are in diffrent sectors
				return false;
			}
			return true;
		}
		return false;
	}
	getLevelIndexDiffrenceFilters(viewContext: SetViewContextEvent['msg']): boolean {
		const filterLevelIndex = this.levels.indexOf(this.filtersViewContext.value.CurrentFocus);
		const newLevelIndex = this.levels.indexOf(viewContext.CurrentFocus);
		if (filterLevelIndex < newLevelIndex) {
			if (
				viewContext.CurrentFocus === ViewContext.Community &&
				this.filtersViewContext.value.CurrentFocus === ViewContext.Sector &&
				viewContext.Sector !== this.filtersViewContext.value.Sector
			) {
				//? Yas island to rs3. They are in diffrent sectors
				return false;
			}
			return true;
		}
		return false;
	}
	switchtoCommunityContext(): void {
		const message = {
			Country: 'UAE',
			City: '02',
			Sector: this.viewContextState.value.Sector,
			Community: this.viewContextState.value.Community,
			Plot: '',
			Building: '',
			Floor: '',
			Unit: '',
			Asset: '',
			CurrentFocus: 'Community',
		};
		this.streamSetViewContext(message as ViewContextState);
	}
	switchtoPreviousViewContext(): void {
		switch (this.currentViewContextState) {
			case ViewContext.Community:
				this.triggerSetBreadcrumbs.next(ViewContext.Sector);
				break;
			case ViewContext.Plot:
				this.triggerSetBreadcrumbs.next(ViewContext.Community);
				break;
			case ViewContext.Building:
				this.triggerSetBreadcrumbs.next(ViewContext.Plot);
				break;
			case ViewContext.Floor:
				this.triggerSetBreadcrumbs.next(ViewContext.Building);
				break;
			case ViewContext.Unit:
				this.triggerSetBreadcrumbs.next(ViewContext.Floor);
				break;
			default:
				break;
		}
	}
	switchtoPreviousViewContextHeatMap(): void {
		switch (this.currentViewContextState) {
			case ViewContext.Sector:
				this.triggerSetBreadcrumbs.next(ViewContext.City);
				break;
			case ViewContext.Community:
				this.triggerSetBreadcrumbs.next(ViewContext.Sector);
				break;
			default:
				break;
		}
	}
	checkToSwitchContextToBuilding(): boolean {
		if (
			this.checkIfInREICommunity() &&
			this.switchToBuildingFromCommunity &&
			this.assetToHighlight !== ''
		) {
			if (typeof this.assetToHighlight === 'object') {
				this.assetToHighlight.CanZoomOnSVC = true;
				this.streamSetViewContext(
					this.assetToHighlight as ViewContextState,
					true,
					CREATOR.Blackshark,
				);
			}
			this.assetToHighlight = '';
			this.switchToBuildingFromCommunity = false;
			this.isInAssetSelectedMode = true;
			return true;
		} else {
			this.assetToHighlight = '';
			this.switchToBuildingFromCommunity = false;
			return false;
		}
	}

	switchViewContextToBSKBuilding(buildingId: string): void {
		if (buildingId !== '') {
			this.switchToBuildingFromCommunity = false;
			this.assetToHighlight = '';
			let message: ViewContextState = {
				context: ViewContext.Building,
				Country: 'UAE',
				City: '02',
				Sector: '',
				Community: '',
				Plot: '',
				Building: buildingId,
				Floor: '',
				Unit: '',
				Asset: '',
				CurrentFocus: ViewContext.Building,
			};
			if (this.summarySubscription) {
				this.summarySubscription.unsubscribe();
			}
			this.summarySubscription = this._http
				.getREIInsightsData({
					assetId: buildingId,
					floorId: '',
					type: ViewContext.Building,
				})
				.subscribe(
					async (data: REIInsightsData) => {
						if (!this.checkIfSummaryResponseValid(data)) {
							this.toasterMessage.next({
								type: 'error',
								message: this.toastrStaticMessage.cannotSetViewContext,
							});
							return;
						}
						message.Sector = data.sector;
						message.Community = data.community;
						if (
							data.city === '02' &&
							data.sector === this.viewContextState.value.Sector &&
							data.community === this.viewContextState.value.Community
						) {
							message.is_bsk = true;
							this.viewContextState.next(message as ViewContextState);
							this.requestHistoryUpdate.next(true);
						} else {
							message.CurrentFocus = ViewContext.Community;
							message.Sector = data.sector;
							message.Community = data.community;
							delete message.context;
							if (this.filtersAvailable.value || this.heatMapAvailable.value) {
								message.Building = '';
								this.streamSetViewContext(message, true, CREATOR.Default);
								message.Building = buildingId;
								message.is_bsk = true;
								this.tempViewContextBSK = message;
								this.tempViewContextBSK.CanZoomOnSVC = false;
								return;
							}
							message.CanZoomOnSVC = false;
							this.streamSetViewContext(message, true, CREATOR.Blackshark);
							message.context = ViewContext.Building;
							message.CurrentFocus = ViewContext.Building;
							message.is_bsk = true;
							delete message.CanZoomOnSVC;
							this.viewContextState.next(message as ViewContextState);
							// update history for bsk building
							this.requestHistoryUpdate.next(true);
						}
					},
					(error) => {
						this.toasterMessage.next({
							type: 'error',
							message: this.toastrStaticMessage.cannotSetViewContext,
						});
					},
				);
		} else {
			this.toasterMessage.next({
				type: 'error',
				message: this.toastrStaticMessage.cannotSetViewContext,
			});
		}
	}
	checkIfSummaryResponseValid(data: REIInsightsData): boolean {
		if (data.city === 'NA' || data.sector === 'NA' || data.community === 'NA') {
			return false;
		}
		return true;
	}

	setCustomViewContextForBuilding(buildingId: string): void {
		if (buildingId !== '') {
			let message: ViewContextState = {
				context: ViewContext.Building,
				Country: 'UAE',
				City: '02',
				Sector: '',
				Community: '',
				Plot: '',
				Building: buildingId,
				Floor: '',
				Unit: '',
				Asset: '',
				CurrentFocus: ViewContext.Building,
				is_bsk: true,
			};
			this._http
				.getREIInsightsData({
					assetId: buildingId,
					floorId: '',
					type: ViewContext.Building,
				})
				.subscribe(
					(data: REIInsightsData) => {
						message.Sector = data.sector;
						message.Community = data.community;
						message.Plot = data.plot;
						this.viewContextState.next(message as ViewContextState);
						// update history for bsk builidng
						this.requestHistoryUpdate.next(true);
					},
					(error) => {
						this.toasterMessage.next({
							type: 'error',
							message: this.toastrStaticMessage.cannotSetViewContext,
						});
					},
				);
		} else {
			this.toasterMessage.next({
				type: 'error',
				message: this.toastrStaticMessage.cannotSetViewContext,
			});
		}
	}
	switchToCommunityById(cityId: string, sectorId: string, communityId: string): boolean {
		if (cityId && sectorId && communityId) {
			let message = {
				Country: 'UAE',
				City: cityId,
				Sector: sectorId,
				Community: communityId,
				Plot: '',
				Building: '',
				Floor: '',
				Unit: '',
				Asset: '',
				CurrentFocus: ViewContext.Community,
			};
			this.streamSetViewContext(message as ViewContextState);
			return true;
		} else {
			this.toasterMessage.next({
				type: 'error',
				message: this.toastrStaticMessage.cannotSetViewContext,
			});
			return false;
		}
	}

	switchToBreadcrumbCommunity(): void {
		const message = {
			Country: 'UAE',
			City: '02',
			Sector: this.viewContextState.value.Sector,
			Community: this.viewContextState.value.Community,
			Plot: '',
			Building: '',
			Floor: '',
			Unit: '',
			Asset: '',
			CurrentFocus: ViewContext.Community,
		};
		this.streamSetViewContext(message as ViewContextState);
	}

	switchtoPlotContext(): void {
		this.triggerSetBreadcrumbs.next(ViewContext.Plot);
	}

	implementFilters(msg: ViewContextState, reapply = false) {
		if (this.showGameSpinner.value === false && msg.CurrentFocus !== ViewContext.Plot) {
			this.showGameSpinner.next(true);
		}
		let returnLevel = this.levels[this.levels.indexOf(msg.CurrentFocus) + 1];

		let filterData = this.filteredData.value[returnLevel.toLowerCase()]
			? this.filteredData.value[returnLevel.toLowerCase()]
				.filter((asset: any) => {
					if (msg.CurrentFocus == ViewContext.City) return true;
					if (returnLevel.toLowerCase() !== ViewContext.Plot.toLowerCase()) {
						return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
					} else {
						return (
							asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus] &&
							(asset.is_in_game === '1' || asset.is_in_game === 1)
						);
					}
				})
				.map((asset: any) => asset.id)
			: [];
		let assetsName = this.getFilteredAssetsName(returnLevel, msg);
		if (this.checkIfInREICommunity()) {
			let returnLevelBuilding = this.levels[this.levels.indexOf(msg.CurrentFocus) + 2]; //?set to building
			assetsName = this.getFilteredAssetsName(returnLevelBuilding, msg);
			let buildingData = this.filteredData.value[returnLevelBuilding.toLowerCase()]
				? this.filteredData.value[returnLevelBuilding.toLowerCase()]
					.filter((asset: any) => {
						if (msg.CurrentFocus == ViewContext.City) return true;
						return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
					})
					.map((asset: any) => asset.id)
				: [];
			if (buildingData.length == 0) {
				this.handleCannotApplyFilters();
				return;
			}
			this.setFilteredAreas(msg, returnLevelBuilding, assetsName);
			this.setFilter(buildingData, returnLevelBuilding);
			this.implementHeatMapOnBuilding(buildingData);
			return;
		}
		if (filterData.length == 0 && msg.CurrentFocus != ViewContext.Plot) {
			//?if data array is empty filter cannot be applied
			this.handleCannotApplyFilters();
			return;
		}
		if (msg.CurrentFocus != ViewContext.Plot) {
			//?setting filtered areas data
			this.setFilteredAreas(msg, returnLevel, assetsName);
			this.setFilter(filterData, returnLevel);
		}
	}

	setFilteredAreas(msg: ViewContextState, returnLevel: ViewContext, assetsName: any[]): void {
		//?sort assets name in ASC order
		const assetsNameSorted = assetsName.sort((a: any, b: any) => {
			if (a.name < b.name) {
				return -1;
			}
			if (a.name > b.name) {
				return 1;
			}
			return 0;
		});
		if (!this.checkIfInREICommunity()) {
			if (returnLevel == ViewContext.Community) {
				this.filteredCountTitle.next(`Filtered Communities (${assetsName.length})`);
			} else {
				this.filteredCountTitle.next(`Filtered ${returnLevel}s (${assetsName.length})`);
			}

			this.filteredAreas.next(assetsNameSorted);
		} else {
			this.filteredCountTitle.next(`Filtered Buildings (${assetsName.length})`);
			this.filteredAreas.next(assetsNameSorted);
		}
	}
	getNextLevel(): string {
		const currentFocus = this.viewContextState.value.CurrentFocus;
		// REI mode has special logic for community level
		if (this.isInREI() && currentFocus === ViewContext.Community) {
			return this.levels[this.levels.indexOf(currentFocus) + 2];
		}
		// GIS and LBM modes use standard progression
		return this.levels[this.levels.indexOf(currentFocus) + 1];
	}
	getFilterCountAreasInPlural(context: string): string {
		switch (context) {
			case ViewContext.City:
				return 'Cities';
			case ViewContext.Sector:
				return 'Sectors';
			case ViewContext.Community:
				return 'Communities';
			case ViewContext.Building:
				return 'Buildings';
			case ViewContext.Floor:
				return 'Floors';
			case ViewContext.Unit:
				return 'Units';
			default:
				return '';
		}
	}

	handleCannotApplyFilters(): void {
		this.loadApplicationTypeSummary.next(true);
		this.toasterMessage.next({
			type: 'info',
			message: this.toastrStaticMessage.noFilterDataAvailable,
		});
		this.showGameSpinner.next(false);
		this.filteredCountTitle.next('');
		this.filteredAreas.next([]);
		this.disableFilterAreasPopup.next(true);
	}

	getFilteredAssetsName(returnLevel: any, msg: ViewContextState) {
		let assetsName = [];
		assetsName = this.filteredData.value[returnLevel.toLowerCase()]
			? this.filteredData.value[returnLevel.toLowerCase()]
				.filter((asset: any) => {
					if (msg.CurrentFocus == ViewContext.City) return true;
					return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
				})
				.map((asset: any) => {
					if (msg.CurrentFocus == ViewContext.Community) {
						return {
							id: asset.id,
							name: asset.id,
							is_in_game: asset?.is_in_game || '0',
						};
					} else {
						return {
							id: asset.id,
							name: asset.name,
							is_in_game: asset?.is_in_game || '0',
						};
					}
				})
			: [];
		return assetsName;
	}

	implementHeatMap(msg: ViewContextState, reapply = false, plotStatus:any) {
		if (this.showGameSpinner.value === false && msg.CurrentFocus !== ViewContext.Plot) {
			this.showGameSpinner.next(true);
		}
		let returnLevel = this.levels[this.levels.indexOf(msg.CurrentFocus) + 1];
		if (!reapply) {
			if (this.isHeatMap && this.lastLevel == returnLevel) {
				// return;
			}
		}
		this.heatLvls.next([0, 0]);

		let currentLevelData;
		if (this.checkIfInREICommunity()) {
			returnLevel = this.levels[this.levels.indexOf(msg.CurrentFocus) + 2]; //?set to building
			currentLevelData = this.heatMapData.value[returnLevel.toLowerCase()]
				? this.heatMapData.value[returnLevel.toLowerCase()].filter((asset: any) => {
					return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
				})
				: [];
		} else {
			currentLevelData = this.heatMapData.value[returnLevel.toLowerCase()]
				? this.heatMapData.value[returnLevel.toLowerCase()].filter((asset: any) => {
					if (msg.CurrentFocus == ViewContext.City) return true;
					return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
				})
				: [];

				if (!plotStatus) {
					if (returnLevel.toLowerCase() === ViewContext.Plot.toLowerCase()) {
						currentLevelData = currentLevelData.filter((asset: any) => {
							return asset.is_in_game == 1 || asset.is_in_game == '1';
						});
					}
				}
		}

		let heatmapData = currentLevelData.map((asset: any) => {
			return asset;
		});

		let sortedHeatLevels = currentLevelData
			.map((eachItem: any) => eachItem.count)
			.sort((min: number, max: number) => min - max);

		this.heatLvls.next([
			sortedHeatLevels[0] || 0,
			sortedHeatLevels[sortedHeatLevels.length - 1] || 0,
		]);

		let min = Math.abs(+this.heatLvls.value[0]);
		let max = Math.abs(+this.heatLvls.value[1]);

		const partitions = [];
		const range = max - min;
		const step = range / 5;

		for (let i = 0; i < 5; i++) {
			let val = min + i * step;
			partitions.push(Math.abs(val));
		}

		partitions.push(max);

		this.setHeatMap(heatmapData, returnLevel, partitions, plotStatus);
	}

	public setHeatMap(heatmap: any, returnLevel: ViewContext, parts: number[], plotStatus:any) {
		if (this.checkIfInREICommunity()) {
			this.disableBoundaries();
		} else {
			this.enableBoundaryIfNotEnabled();
		}
		let creator = this.checkIfInREICommunity() ? CREATOR.Blackshark : CREATOR.Default;
		let message: any;
		if (!plotStatus) {
			message = {
				cmd: 'Heatmap',
				type: 'request',
				msg: {
					ViewContext: returnLevel,
					xray: false,
					assetvalues: [],
					creator: creator,
					Lut: [
											// { R: 86, G: 127, B: 157, A: 255 },
						{ R: 216, G: 56, B: 21, A: 255 },
						{ R: 216, G: 121, B: 79, A: 255 },
						{ R: 224, G: 166, B: 123, A: 255 },
						{ R: 232, G: 190, B: 121, A: 255 },
						{ R: 190, G: 198, B: 145, A: 255 },
						{ R: 144, G: 178, B: 122, A: 255 },
						{ R: 110, G: 150, B: 85, A: 255 },
					],
					// Lut: [	// yellow - green - red
					// 	{ R: 232, G: 190, B: 121, A: 255 },
					// 	{ R: 224, G: 166, B: 123, A: 255 },
					// 	{ R: 190, G: 198, B: 145, A: 255 },
					// 	{ R: 144, G: 178, B: 122, A: 255 },
					// 	{ R: 110, G: 150, B: 85, A: 255 },
					// 	{ R: 216, G: 121, B: 79, A: 255 },
					// 	{ R: 216, G: 56, B: 21, A: 255 },
					// ],
				},
			};
		} else {
			message = {
				cmd: 'Heatmap',
				type: 'request',
				msg: {
					ViewContext: returnLevel,
					xray: false,
					assetvalues: [],
					creator: creator,
					Lut: [
						{ R: 86, G: 127, B: 157, A: 255 },
						{ R: 110, G: 150, B: 85, A: 1 },
						{ R: 232, G: 190, B: 121, A: 1 },
						{ R: 126, G: 111, B: 134, A: 1 },
						{ R: 216, G: 56, B: 21, A: 1 },
						{ R: 190, G: 198, B: 145, A: 255 },
						{ R: 144, G: 178, B: 122, A: 255 },
						{ R: 110, G: 150, B: 85, A: 255 },
					],
				},
			};
		}
		this.isHeatMap = true;

		heatmap.forEach((asset: any) => {
			let index = 1;
			while (index < parts.length && parts[index] <= asset.count) {
				index++;
			}
			let lutIndex = index;
			if (!plotStatus) {
				if (parts[parts.length - 1] == 0) {
					lutIndex = 1;
				}
			} else {
				if (asset.count == 99) {
					lutIndex = 1;
				} else if (asset.count == 74) {
					lutIndex = 2;
				}else if (asset.count == 49) {
					lutIndex = 3;
				}else if (asset.count == 24) {
					lutIndex = 4;
				} else {
					lutIndex = 5;
				}
			}
			message.msg.assetvalues.push({
				assetID: asset.id,
				lutindex: lutIndex,
			});
		});
		message.msg.assetvalues = message.msg.assetvalues.filter((asset: any) => {
			return asset.assetID != 'None';
		});
		this.heatmapMsg = message;
		this.isHeatmapModified = false;
		this.loadApplicationTypeSummary.next(true);
		if (this.isREIHeatmapAllowed()) {
			if (message.msg.assetvalues.length === 0) {
				this.toasterMessage.next({
					type: 'info',
					message: this.toastrStaticMessage.cannotApplyHeatMap,
				});
				this.heatmapMsg = {};
				this.heatmapError.next(true); // hide heatmap summary if the heatmap can't be applied
				this.showGameSpinner.next(false);
				return;
			}
			console.log('message:', message);
			this.resetInteractions();
		} else {
			this.toasterMessage.next({
				type: 'info',
				message: this.toastrStaticMessage.cannotApplyHeatMap,
			});
			this.switchtoPreviousViewContext();
			this.showGameSpinner.next(false);
			return;
		}
	}

	highlightAsset(viewContext: string | ViewContextState): any {
		if (viewContext !== '') {
			return viewContext;
		} else {
			return false;
		}
	}

	public implementHeatMapOnBuilding(heatmap: any): void {
		// Lut: [{ R: 232, G: 190, B: 121, A: 255 }], //?crimson
		// Lut: [{ R: 214, G: 208, B: 21, A: 255 }], //?yellow

		const message: any = {
			cmd: 'Heatmap',
			type: 'request',
			msg: {
				ViewContext: 'Building',
				xray: false,
				assetvalues: [],
				Lut: [{ R: 65, G: 156, B: 9, A: 255 }], //?green
				Creator: CREATOR.Blackshark,
			},
		};
		heatmap.forEach((asset: any) => {
			let lutIndex = 0;
			message.msg.assetvalues.push({
				assetID: asset,
				lutindex: lutIndex,
			});
		});

		message.msg.assetvalues = message.msg.assetvalues.filter((asset: any) => {
			return asset.assetID != 'None';
		});
		this.isFilterApplied = true;
		this.filtersAvailable.next(true);
		this.filterMessage = message;
		this.resetInteractions();
	}
	enableBoundaryIfNotEnabled(): void {
		if (this._boundaryService.toggleBoundaries.value[0] === false) {
			this._boundaryService.toggleBoundaries.next([true, true]);
		}
	}

	updateHistory(msg: ViewContextState): void {
		this.isUpdatingHIstory = true;
		if (this.currentUser.value.userData.email && this.isConnected.value === true) {
			let userHistory: UserHistory = {
				search_id: msg[msg.CurrentFocus],
				label: msg.CurrentFocus,
				name: '',
				city_id: msg.City || '',
				city_name: '',
				sector_id: msg.Sector || '',
				sector_name: '',
				community_id: msg.Community || '',
				community_name: '',
				plot_id: msg.Plot || '',
				plot_name: '',
				building_id: msg.Building || '',
				building_name: '',
				application: this.applicationType.value,
				floor_id: msg.Floor || '',
				floor_name: '',
				unit_id: msg.Unit || '',
				unit_name: '',
			};

			switch (msg.CurrentFocus) {
				case ViewContext.City:
					userHistory.sector_id = '';
					userHistory.community_id = '';
					userHistory.plot_id = '';
					userHistory.building_id = '';
					userHistory.floor_id = '';
					userHistory.unit_id = '';
					break;
				case ViewContext.Sector:
					userHistory.community_id = '';
					userHistory.plot_id = '';
					userHistory.building_id = '';
					userHistory.floor_id = '';
					userHistory.unit_id = '';
					break;
				case ViewContext.Community:
					userHistory.plot_id = '';
					userHistory.building_id = '';
					userHistory.floor_id = '';
					userHistory.unit_id = '';
					break;
				case ViewContext.Plot:
					userHistory.building_id = '';
					userHistory.floor_id = '';
					userHistory.unit_id = '';
					break;
				case ViewContext.Building:
					userHistory.floor_id = '';
					userHistory.unit_id = '';
					userHistory.bsk_id = msg.is_bsk ? 1 : 0;
					break;
				case ViewContext.Floor:
					userHistory.unit_id = '';
					break;
				default:
					break;
			}

			// add the data to local history state
			this.historyStore.dispatch(
				addHistory({
					item: userHistory,
					layerType: userHistory.label.toLowerCase() as Lowercase<ViewContext>,
					id: userHistory.search_id,
					cityId: userHistory.city_id as string,
				}),
			);
			this._http
				.postRecentHistory(userHistory)
				.subscribe(
					(data: UserHistory) => {
						this.isUpdatingHIstory = false;
						setTimeout(() => {
							this.fetchHistory.next(true);
						}, 3000);
					},
					(err) => {
						this.isUpdatingHIstory = false;
					},
				);
		} else {
			this.isUpdatingHIstory = false;
		}
	}

	public clearAppState(): void {
		this.viewContextState.next({
			CurrentFocus: ViewContext.City,
			Country: 'UAE',
			City: '',
			Community: '',
			Sector: '',
			Plot: '',
			Building: '',
			Floor: '',
			Unit: '',
			Asset: '',
			is_bsk: false,
		});
		this.hoverChartState.next({
			AssetID: '',
			ObjectType: ViewContext.City,
			CameraDistance: this.hoverChartState.value.CameraDistance,
			CameraDistanceMax: this.hoverChartState.value.CameraDistanceMax,
			CameraDistanceMin: this.hoverChartState.value.CameraDistanceMin,
		});
		this.store.dispatch(resetHoverData());
		this.selectedBuildings.next([]);
		this.toolSelectState.next({
			ToolId: ToolId.Leasing_and_Transactions,
			Selected: true,
		});
		this.freezeTooltip.next(false);
		this.bitrateRequestFlag = false;
	}

	public getViewContext(): void {
		const message = {
			cmd: 'GetViewContext',
			type: 'request',
			msg: { status: 'currently unused' },
		};
		console.log('Sending GetViewContext message: ', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}

	public setZoomLevel(level: number): void {
		const message = {
			cmd: 'OverrideSettings',
			type: 'notif',
			msg: { CameraDistance: level },
		};
		console.log('Sending OverrideSettings message: ', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}

	public getZoomLevel(): void {
		window.addEventListener(
			'CameraRangeAlert',
			(msg) => {
				console.log('CameraRangeAlert message: ', msg);
			},
			true,
		);
	}

	setFilter(assets: string[], focusLevel = ViewContext.City) {
		if (this.isBoundaryAllowed()) {
			this.enableBoundaryIfNotEnabled();
		}
		const filterObj: any = {
			ViewContext: focusLevel,
			xray: true,
			Emissivity: 2,
			assetvalues: [],
			Lut: [
				// { R: 224, G: 141, B: 123, A: 255 },
				{ R: 225, G: 0, B: 0, A: 255 },
				// { R: 224, G: 141, B: 123, A: 255 },
				{ R: 225, G: 0, B: 0, A: 255 },
			],
		};
		assets.forEach((id) => {
			filterObj.assetvalues.push({
				assetID: id,
				lutindex: 1,
			});
		});
		filterObj.assetvalues = filterObj.assetvalues.filter((asset: any) => {
			return asset.assetID != 'None';
		});
		const message = {
			cmd: 'Outlinemap',
			type: 'request',
			msg: filterObj,
		};
		this.isFilterApplied = true;
		this.filterMessage = message;
		this.filtersAvailable.next(true);
		this.loadApplicationTypeSummary.next(true);
		if (!this.isViewContextAfterREICommunity()) {
			this.resetInteractions();
		}
	}

	public clickPlay(timeout: number): void {
		setTimeout(() => {
			const playOverlay = document.querySelector('#videoPlayOverlay') as HTMLDivElement;
			if (playOverlay) {
				playOverlay.click();
			}
		}, timeout);
	}

	public resetInteractions(reset = false, reapply = false) {
		const message = {
			cmd: 'SetInteractionMode',
			type: 'request',
			msg: {
				Mode: 'Interaction',
			},
		};
		if (reset) {
			this.isHeatMap = false;
			this.heatmapMsg = null;
			this.isFilterApplied = false;
			this.filterMessage = null;
			this.filteredCountTitle.next('');
			this.filteredAreas.next([]);
			if (reapply === false) {
				this.heatMapData.next({});
				this.resetAllFilterDataToDefault();
			}
		}
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}

	resetAllFilterDataToDefault(): void {
		this.selectedFilters.next({
			...lbmFilterForm,
		});
		this.selectedREIFilters.next({
			...reiFilterForm,
		});
	}

	public getToolbarColor(): { color: string; class: string; background: string } {
		const toolId = this.toolSelectState.value;
		if (!toolId) {
			return { color: '#fff', class: 'highlight-text', background: '#1c1c1c' };
		}
		switch (toolId?.ToolId) {
			case ToolId.Portfolio:
				return { color: '#8cc9dc', class: 'portfolio', background: '#01342f' };
			case ToolId.Development:
				return { color: '#03A9F4', class: 'development', background: '#014662' };
			case ToolId.Leasing_and_Transactions:
				return { color: '#8cc9dc', class: 'portfolio', background: '#01342f' };
			case ToolId.Space:
				return { color: '#FF5722', class: 'space', background: '#511c0b' };
			case ToolId.Maintenance:
				return { color: '#FFC107', class: 'maintenance', background: '#5e4504' };
			case ToolId.Sustainability:
				return { color: '#9Be788', class: 'sustainability', background: '#395231' };
			case ToolId.Market_Intelligence:
				return { color: '#FF9800', class: 'market-intelligence', background: '#623c00' };
			case ToolId.Alternative_Data:
				return { color: '#E91E63', class: 'alternative-data', background: '#500b24' };
			default:
				return { color: '#fff', class: 'highlight-text', background: '#1c1c1c' };
		}
	}

	public getNextContext(currentLevel: string): string {
		switch (currentLevel) {
			case ViewContext.City:
				return ViewContext.Sector;
			case ViewContext.Sector:
				return ViewContext.Community;
			case ViewContext.Community:
				return ViewContext.Plot;
			case ViewContext.Plot:
				return ViewContext.Building;
			case ViewContext.Building:
				return ViewContext.Unit;
			case ViewContext.Unit:
				return ViewContext.Asset;
			default:
				return ViewContext.City;
		}
	}

	public setDaylightScale(newTime: number): void {
		const day = new Date().getDate();
		const month = new Date().getMonth();
		const year = new Date().getFullYear();
		const message = {
			cmd: 'SetEnvironment',
			type: 'notif',
			msg: { SolarTime: newTime, Day: day, Month: month, Year: year },
		};
		console.log('Sending environment message: ', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	public setOverrideSettings(zoomSpeed: number, panSpeed: number, initial: boolean = true): void {



		let msg = { CameraZoomSpeed: zoomSpeed, CameraPanSpeed: panSpeed, PreferLookNorth: true };
		const message = {
			cmd: 'OverrideSettings',
			type: 'notif',
			msg,
		};
		console.log('Sending override settings message: ', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	public cameraDistanceStringToNumber(value: any): number {
		//"30,222,332" to 30222332
		//if value is integer return it
		if (Number.isInteger(value)) return value;
		return +value.split(',').join('');
	}

	disableBoundaries(): void {
		this._boundaryService.toggleBoundaries.next([false, true]);
	}
	checkIfInREICommunity(): boolean {
		if (
			this.applicationType.value === APP_TYPE.REI &&
			this.viewContextState.value.CurrentFocus === ViewContext.Community
		) {
			return true;
		}
		return false;
	}
	checkIfInREIPlot(viewContext: SetViewContextEvent): boolean {
		if (
			this.applicationType.value === APP_TYPE.REI &&
			viewContext.msg.CurrentFocus === ViewContext.Plot
		) {
			return true;
		}
		return false;
	}
	checkToEnableBoundary(viewContext: SetViewContextEvent): void {
		if (this.checkIfInREICommunity()) {
			this.disableBoundaries();
			this.isBoundaryDisableForCommunity = true;
		} else {
			if (this.applicationType.value === APP_TYPE.REI && this.isBoundaryDisableForCommunity) {
				this.enableBoundaryIfDisabled();
				this.isBoundaryDisableForCommunity = false;
			}
		}
	}

	// for REI heatmap, restrict user to navigate to community for the specific cases
	isREIHeatmapAllowed(): boolean {
		if (this.applicationType.value === APP_TYPE.REI) {
			if (
				this.viewContextState.value.CurrentFocus === ViewContext.Community &&
				(this.selectedHeatmapType.toLowerCase() === 'supply & demand' ||
					this.selectedHeatmapType.toLowerCase() === 'number of buildings' ||
					this.selectedHeatmapType.toLowerCase() === 'rent' ||
					this.selectedHeatmapType.toLowerCase() === 'sales')
			) {
				return false;
			} else {
				return true;
			}
		}
		return true;
	}
	isBoundaryAllowed(): boolean {
		if (this.isViewContextAfterREICommunity()) {
			return false;
		}
		return true;
	}
	isInREI(): boolean {
		if (this.applicationType.value === APP_TYPE.REI) {
			return true;
		}
		return false;
	}
	isInGIS(): boolean {
		if (this.applicationType.value === APP_TYPE.GIS) {
			return true;
		}
		return false;
	}
	isInLBM(): boolean {
		if (this.applicationType.value === APP_TYPE.LBM) {
			return true;
		}
		return false;
	}
	isViewContextAfterREICommunity(): boolean {
		if (
			this.applicationType.value === APP_TYPE.REI &&
			(this.viewContextState.value.CurrentFocus === ViewContext.Community ||
				this.viewContextState.value.CurrentFocus === ViewContext.Plot ||
				this.viewContextState.value.CurrentFocus === ViewContext.Building ||
				this.viewContextState.value.CurrentFocus === ViewContext.Floor ||
				this.viewContextState.value.CurrentFocus === ViewContext.Unit)
		) {
			return true;
		}
		return false;
	}
	isViewContextAfterPlot(): boolean {
		if (
			this.viewContextState.value.CurrentFocus === ViewContext.Plot ||
			this.viewContextState.value.CurrentFocus === ViewContext.Building ||
			this.viewContextState.value.CurrentFocus === ViewContext.Floor ||
			this.viewContextState.value.CurrentFocus === ViewContext.Unit
		) {
			return true;
		}
		return false;
	}
	enableBoundaryIfDisabled(): void {
		if (this._boundaryService.toggleBoundaries.value[0] === false) {
			this._boundaryService.toggleBoundaries.next([true, true]);
		}
	}
	hasHeatmapDataForReturnLevel(msg: any): boolean {
		let returnLevel = this.checkIfInREICommunity()
			? this.levels[this.levels.indexOf(msg.CurrentFocus) + 2]
			: this.levels[this.levels.indexOf(msg.CurrentFocus) + 1];
		if (this.heatMapData.value[returnLevel.toLowerCase()]) {
			let currentLevelData = this.heatMapData.value[returnLevel.toLowerCase()]
				? this.heatMapData.value[returnLevel.toLowerCase()].filter((asset: any) => {
					if (msg.CurrentFocus == ViewContext.City) return true;
					return asset[msg.CurrentFocus.toLowerCase()] == msg[msg.CurrentFocus];
				})
				: [];
			return currentLevelData.length > 0 ? true : false;
		}
		return false;
	}

	//switching to flymode
	sentFlyModeEvent(state: boolean): void {
		const message = {
			cmd: 'FlyingCamera',
			type: 'notif',
			msg: { Enable: state },
		};
		console.log('FlyingCamera:', message);
		this.sendMessageToIframe(state?this.messageToIframeToDisableCursor:this.messageToIframeToEnableCursor)
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	//switching to character view
	sentCharacterViewEvent(state: boolean): void {
		const message = {
			cmd: 'SwitchtoCharacterView',
			type: 'notif',
			msg: { Enable: state },
		};
		console.log('CharacterView:', message);
		this.sendMessageToIframe(this.messageToIframeToEnableCursor)
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	setFlyModeMouseSpeed(speed: number): void {
		const message = {
			cmd: 'FlyingCameraRotationSpeed',
			type: 'notif',
			msg: { Speed: speed },
		};
		console.log('FlyingCameraRotationSpeed:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	//Gateways Integration
	selectGatewayLocation(state: boolean): void {
		const message = {
			cmd: 'GatewayActive',
			type: 'notif',
			msg: { Enable: state },
		};
		console.log('GatewayActive:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	showGatewayBoundry(state: boolean): void {
		const message = {
			cmd: 'GetBoundaryVisibility',
			type: 'response',
			msg: { City: state, Sector: state, Community: state, Plot: state, Unit: state },
		};
		console.log('GetBoundaryVisibility:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	addGatewayPoint(state: boolean): void {
		const message = {
			cmd: 'GatewayAddpoint',
			type: 'notif',
			msg: { active: state },
		};
		console.log('GatewayAddpoint:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	closeGatewayPolygon(state: boolean): void {
		const message = {
			cmd: 'GatewayClose',
			type: 'notif',
			msg: { Close: state },
		};
		console.log('GatewayClose:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	removeGatewayPolygon(state: boolean): void {
		const message = {
			cmd: 'GatewayClear',
			type: 'notif',
			msg: { Delete: state },
		};
		console.log('GatewayClear:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	undoGatewayPolygon(state: boolean): void {
		const message = {
			cmd: 'GatewayUndo',
			type: 'notif',
			msg: { Delete: state },
		};
		console.log('GatewayUndo:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	redoGatewayPolygon(state: boolean): void {
		const message = {
			cmd: 'GatewayRedo',
			type: 'notif',
			msg: { Delete: state },
		};
		console.log('GatewayRedo:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	requestGatewayPolygon(): void {
		const message = {
			cmd: 'GWGetSavedCordinated',
			type: 'notif',
			msg: {},
		};
		console.log('GWGetSavedCordinated:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	getGatewayPolygon(state: boolean): void {
		const message = {
			cmd: 'GWSavedData',
			type: 'notif',
			msg: { Delete: state },
		};
		console.log('GWSavedData:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
	takeScreenshot(state: boolean): void {
		const message = {
			cmd: 'UtilsScreenShot',
			type: 'notif',
			msg: { Enabled: state , SignedUrl: 'https://google.com' , expiryTime: 3600},
		};
		console.log('UtilsScreenShot:', message);
		//sendToStreamer(message);
		this.sendThisMessageToStreamer(message);
	}
}
